import { DollarSign } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import type { BusinessRecurringRevenue, BusinessRevenue, BusinessRevenueCurrency } from '../../types/businessMetrics';
import { buildRevenueBucketRows, formatBusinessCount, formatBusinessMoney } from '../../types/businessMetricsView';
import { BusinessPanel, NotApplicableNote, StatGrid, StatTile } from './businessPanelParts';

const SOURCE_LABEL: Record<BusinessRevenue['source'], string> = {
  PADDLE_LEDGER: 'Paddle 거래 원장',
  ORDERS_FIRST_PAYMENT: 'pay 주문(첫 결제)',
};

export const LIST_PRICE_LABEL = '정가 기준';
export const LIST_PRICE_NOTE = 'MRR·ARR 은 정가 기준입니다. FOUNDER50 같은 할인이 적용된 구독은 실제 청구액과 다를 수 있습니다.';

function CurrencyRevenueCard({ block }: { block: BusinessRevenueCurrency }) {
  const rows = buildRevenueBucketRows(block);
  const money = (value: number | null) => formatBusinessMoney(value, block.currency);
  return (
    <div className="space-y-3" data-testid={`business-revenue-${block.currency}`}>
      <p className="text-sm font-semibold">{block.currency}</p>
      <StatGrid>
        <StatTile label="결제액" value={money(block.chargedTotal)} hint={`${formatBusinessCount(block.paymentCount)}건`} />
        <StatTile label="환불" value={money(block.refundedTotal)} hint={`${formatBusinessCount(block.refundCount)}건`} />
        <StatTile label="순매출" value={money(block.netRevenue)} tone={block.netRevenue < 0 ? 'negative' : 'default'} />
        <StatTile label="세금" value={money(block.tax)} />
        <StatTile label="할인" value={money(block.discount)} />
        <StatTile label="수수료" value={money(block.fee)} />
        <StatTile label="정산액" value={money(block.earnings)} />
      </StatGrid>
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">기간 안에 결제가 없습니다.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table aria-label={`${block.currency} 기간별 매출`} className="min-w-[520px]">
            <TableHeader>
              <TableRow>
                <TableHead>기간 시작</TableHead>
                <TableHead className="text-right">결제</TableHead>
                <TableHead>결제액</TableHead>
                <TableHead className="text-right">환불</TableHead>
                <TableHead className="text-right">순매출</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.period}>
                  <TableCell className="whitespace-nowrap font-medium">{row.period}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.payments}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 shrink-0 overflow-hidden rounded bg-muted" aria-hidden="true">
                        <div className="h-full rounded bg-primary" style={{ width: `${row.barPercent}%` }} />
                      </div>
                      <span className="whitespace-nowrap tabular-nums">{row.charged}</span>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right tabular-nums">
                    {row.refunded} · {row.refunds}건
                  </TableCell>
                  <TableCell className={`whitespace-nowrap text-right tabular-nums ${row.netNegative ? 'text-destructive' : ''}`}>
                    {row.net}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function RecurringRevenue({ recurring }: { recurring: BusinessRecurringRevenue[] }) {
  if (recurring.length === 0) return null;
  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3" data-testid="business-recurring-revenue">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold">반복 매출</p>
        <Badge variant="warning">{LIST_PRICE_LABEL}</Badge>
      </div>
      {recurring.map((item) => (
        <StatGrid key={item.currency}>
          <StatTile label={`MRR (${item.currency})`} value={formatBusinessMoney(item.mrr, item.currency)} hint={LIST_PRICE_LABEL} />
          <StatTile label={`ARR (${item.currency})`} value={formatBusinessMoney(item.arr, item.currency)} hint={LIST_PRICE_LABEL} />
          <StatTile label="월 구독" value={formatBusinessCount(item.monthlyActive)} />
          <StatTile label="연 구독" value={formatBusinessCount(item.yearlyActive)} />
        </StatGrid>
      ))}
      <p className="text-xs text-muted-foreground">{LIST_PRICE_NOTE}</p>
    </div>
  );
}

export function RevenuePanel({ revenue }: { revenue: BusinessRevenue | null }) {
  if (!revenue) {
    return (
      <BusinessPanel title="매출" testId="business-revenue-panel" icon={DollarSign}>
        <NotApplicableNote reason="이 앱에는 매출 데이터 경로가 없습니다." />
      </BusinessPanel>
    );
  }
  return (
    <BusinessPanel
      title="매출"
      testId="business-revenue-panel"
      icon={DollarSign}
      badge={<Badge variant="outline">{SOURCE_LABEL[revenue.source]}</Badge>}
      footer={revenue.sourceNote}
    >
      <div className="space-y-4">
        {revenue.currencies.length === 0 ? (
          <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">기간 안에 결제가 없습니다.</p>
        ) : (
          revenue.currencies.map((block) => <CurrencyRevenueCard key={block.currency} block={block} />)
        )}
        <RecurringRevenue recurring={revenue.recurring} />
      </div>
    </BusinessPanel>
  );
}
